/**
 * How the Team & roles screens present Identity's roles. Identity owns what each role grants
 * (`Role.permissions`); this only orders, colours and groups them for people.
 */
import type { PermissionInfo, Role, RoleRef } from "@/lib/types";

export const OWNER = "org_owner";

/** Built-in customer roles, most access first. Custom roles follow, by name. */
const ORDER = [
  "org_owner",
  "org_admin",
  "org_editor",
  "org_domain_manager",
  "org_dns_manager",
  "org_billing_manager",
  "org_analytics_viewer",
  "org_gi_viewer",
  "org_viewer",
];

/** Column headings in the compare table. */
export const SHORT_NAME: Record<string, string> = {
  org_owner: "Owner",
  org_admin: "Business Admin",
  org_editor: "Website Mgr",
  org_domain_manager: "Domain Mgr",
  org_dns_manager: "DNS Mgr",
  org_billing_manager: "Billing Mgr",
  org_analytics_viewer: "Analytics",
  org_gi_viewer: "Growth (view)",
  org_viewer: "Read-only",
};

export type RoleTone = "owner" | "admin" | "website" | "domain" | "billing" | "analytics" | "neutral";

const TONE: Record<string, RoleTone> = {
  org_owner: "owner",
  org_admin: "admin",
  org_editor: "website",
  org_domain_manager: "domain",
  org_dns_manager: "domain",
  org_billing_manager: "billing",
  org_analytics_viewer: "analytics",
  org_gi_viewer: "analytics",
};

/**
 * What the built-in roles are for, in the portal's words — shown when Identity's description is
 * missing (older Identity builds) and in the role pickers.
 */
export const ROLE_DESCRIPTION: Record<string, string> = {
  org_dns_manager:
    "Edits DNS records of the domains they're given. No nameserver changes, purchases or transfers.",
  org_gi_viewer:
    "Reads Growth Intelligence evidence, insights and recommendations, without applying or publishing.",
};

export const describeRole = (r: Pick<Role, "key" | "description">) =>
  r.description || ROLE_DESCRIPTION[r.key];

export const roleTone = (key: string | undefined): RoleTone => (key && TONE[key]) || "neutral";

/** Roles that also work as add-ons on top of someone's main role ("Other services"). */
export const ADD_ONS: { key: string; label: string }[] = [
  { key: "org_editor", label: "Website" },
  { key: "org_domain_manager", label: "Domains" },
  { key: "org_dns_manager", label: "DNS" },
  { key: "org_analytics_viewer", label: "Analytics (view)" },
  { key: "org_billing_manager", label: "Billing" },
];

const rank = (r: Pick<Role, "key" | "name">) => {
  const i = ORDER.indexOf(r.key);
  return i === -1 ? ORDER.length : i;
};

export function sortRoles<T extends Pick<Role, "key" | "name">>(roles: T[]): T[] {
  return [...roles].sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
}

/** Organization roles people can hold (platform staff roles never show here). */
export const customerRoles = (roles: Role[]) =>
  sortRoles(roles.filter((r) => r.scope_type === "organization"));

/** A member's main role (the one with the most access) and the add-ons on top of it. */
export function splitRoles<R extends Pick<RoleRef, "key" | "name">>(member: { roles: R[] }) {
  const [main, ...extras] = sortRoles(member.roles);
  return { main, extras };
}

/** Identity's no-escalation rule: you can only hand out permissions you hold yourself. */
export const canGrant = (role: Role, mine: ReadonlySet<string>) =>
  mine.has("*") || role.permissions.every((p) => mine.has(p));

/** Whether `role` adds nothing on top of `base`. */
export const coveredBy = (role: Role, base: Role | undefined) =>
  !!base && role.permissions.every((p) => base.permissions.includes(p));

export const isOwner = (member: { roles: Pick<RoleRef, "key">[] }) =>
  member.roles.some((r) => r.key === OWNER);

/**
 * Permission areas the portal has a feature for. Identity can grant more (e.g. `email:*` for the
 * Email Manager role) before the portal ships a screen for them; those stay out of the matrix.
 */
const AREA_NAMES: Record<string, string> = {
  organization: "Business",
  members: "People",
  roles: "Roles",
  audit: "Audit log",
  websites: "Websites",
  ai: "AI website builder",
  domains: "Domains",
  dns: "DNS",
  deployments: "Hosting",
  analytics: "Analytics",
  billing: "Billing",
  growth: "Growth Intelligence",
};

const AREA_ORDER = [
  "organization",
  "members",
  "roles",
  "audit",
  "websites",
  "ai",
  "domains",
  "dns",
  "deployments",
  "analytics",
  "billing",
  "growth",
];

export interface MatrixCapability {
  permission: string;
  label: string;
  description: string;
  area: string;
  areaLabel: string;
}

/**
 * Builds the matrix from the permissions and roles Identity actually returns. The catalogue is the
 * source of truth for descriptions and scope; the role union determines which customer features are
 * useful to show. Platform-only permissions never leak into the customer portal matrix, and nor do
 * permissions for areas the portal has no feature for yet.
 */
export function matrixCapabilities(roles: Role[], catalogue: PermissionInfo[]): MatrixCapability[] {
  const held = new Set(customerRoles(roles).flatMap((role) => role.permissions));
  const order = new Map(catalogue.map((permission, index) => [permission.key, index]));
  return catalogue
    .filter(
      (permission) =>
        permission.scopes.includes("organization") &&
        held.has(permission.key) &&
        Object.hasOwn(AREA_NAMES, permission.key.split(":", 1)[0] ?? ""),
    )
    .map((permission) => {
      const area = permission.key.split(":", 1)[0] ?? "other";
      const description = permission.description.replace(/\.$/, "") || permissionLabel(permission.key);
      return {
        permission: permission.key,
        label: permissionLabel(permission.key, new Map([[permission.key, description]])),
        description,
        area,
        areaLabel: AREA_NAMES[area] ?? area,
      };
    })
    .sort(
      (a, b) =>
        (AREA_ORDER.indexOf(a.area) === -1 ? AREA_ORDER.length : AREA_ORDER.indexOf(a.area)) -
          (AREA_ORDER.indexOf(b.area) === -1 ? AREA_ORDER.length : AREA_ORDER.indexOf(b.area)) ||
        (order.get(a.permission) ?? Number.MAX_SAFE_INTEGER) -
          (order.get(b.permission) ?? Number.MAX_SAFE_INTEGER),
    );
}

export function matrixGroups(
  roles: Role[],
  catalogue: PermissionInfo[],
): { area: string; label: string; capabilities: MatrixCapability[] }[] {
  const groups: { area: string; label: string; capabilities: MatrixCapability[] }[] = [];
  for (const capability of matrixCapabilities(roles, catalogue)) {
    const existing = groups.find((group) => group.area === capability.area);
    if (existing) existing.capabilities.push(capability);
    else groups.push({ area: capability.area, label: capability.areaLabel, capabilities: [capability] });
  }
  return groups;
}

/** Plain words for single permissions, as the access preview lists them. */
const PERMISSION_LABEL: Record<string, string> = {
  "*": "Full control of the business",
  "websites:read": "View websites",
  "websites:create": "Create websites",
  "websites:update": "Edit and preview websites",
  "websites:publish": "Publish websites",
  "websites:delete": "Delete websites",
  "deployments:read": "View deployments",
  "deployments:manage": "Deploy and manage hosting",
  "analytics:read": "View analytics",
  "domains:read": "View domains",
  "domains:manage": "Connect, verify and remove domains",
  "dns:manage": "Edit DNS records",
  "ai:use": "Use AI generation",
  "billing:read": "View plan and invoices",
  "billing:manage": "Change plan and payment methods",
  "members:read": "See the team",
  "members:invite": "Invite people",
  "members:manage_roles": "Change roles",
  "members:remove": "Remove people",
  "audit:read": "View the audit log and activity",
  "organization:update": "Change business settings",
  "organization:delete": "Delete the business",
};

/** How a permission reads to people: our words, else Identity's description, else its key. */
export function permissionLabel(key: string, catalogue?: ReadonlyMap<string, string>): string {
  const text = PERMISSION_LABEL[key] ?? catalogue?.get(key);
  return text ? text.replace(/\.$/, "") : key;
}

/** "Riley J." — how teammates are listed. */
export function shortName(name: string, email: string): string {
  const [first, ...rest] = name.trim().split(/\s+/).filter(Boolean);
  if (!first) return email;
  const last = rest.at(-1);
  return last ? `${first} ${last.charAt(0).toUpperCase()}.` : first;
}

export const firstName = (name: string, email: string) => name.trim().split(/\s+/)[0] || email;
